import CardCompanyEmployeesSuggested from "@/components/company/card-company-employees-suggested";
import CardCompanyEvents from "@/components/company/card-company-events";
import CardCompanyHeadcountHistory from "@/components/company/card-company-headcount-history";
import CardEmployeeReviews from "@/components/company/card-employee-reviews";
import CardFinancialRatios from "@/components/company/card-financial-ratios";
import { FinancialMetricGrid } from "@/components/company/financial-metric-tile";
import CardGoogleReviews from "@/components/company/card-google-reviews";
import CompanyLegalInfoSection from "@/components/company/company-legal-info-section";
import CompanyMarketSection from "@/components/company/company-market-section";
import CompanyMobileAIInsights from "@/components/company/company-mobile-ai-insights";
import CompanyMobileEmployeeSearchSheet from "@/components/company/company-mobile-employee-search-sheet";
import { CompanySidePanelBody } from "@/components/company/company-preview-panel-content";
import CompanySectionCard, {
  CompanyCardSkeleton,
} from "@/components/company/company-section-card";
import CompanyTechnologiesSection from "@/components/company/company-technologies-section";
import CompanyWebHighlights from "@/components/company/company-web-highlights";
import {
  CompanyWebProfileNotice,
  CompanyWebSynthesisBox,
} from "@/components/company/company-web-profile-view";
import { pickLegalInfoRows } from "@/components/company/shared/company-side-panel-helpers";
import { LazyMount } from "@/components/lazy-mount";
import { Button } from "@/components/ui/button";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useCompanyEnrichment } from "@/hooks/useCompanyEnrichment";
import { useCompanyPageWebProfile } from "@/hooks/useCompanyPageWebProfile";
import { useTranslation } from "@/hooks/useTranslation";
import { webFigureTiles } from "@/components/company/company-web-figure-tiles";
import { COMPANY_CARD_SECTIONS } from "@/lib/company-card-sections";
import {
  hasCompanyEventsData,
  hasHeadcountHistoryData,
} from "@/lib/company-events";
import {
  hasEmployeeReviewsData,
  hasFinancialRatiosData,
  hasGoogleReviewsData,
  hasMarketData,
  hasTechnologiesData,
} from "@/lib/company-data-availability";
import {
  settledWebKeyData,
  webHighlightsOf,
  webSourcesOf,
  webSynthesisOf,
} from "@/stores/company-web-preview-store";
import { getGoogleMapsEmbedQuery } from "@/utils/google-maps";
import type { CompanyDTO, CompanySectionKey } from "@shaarp/common";
import { MailIcon, PhoneIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

/**
 * A block of the body, waiting on its own sections: its skeleton while they
 * run and it has nothing yet, then the block, or nothing.
 */
function MobileSectionCard({
  sections,
  hasData,
  children,
}: {
  readonly sections: readonly CompanySectionKey[];
  readonly hasData: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className="px-4 empty:hidden">
      <CompanySectionCard
        sections={sections}
        hasData={hasData}
        skeleton={<CompanyCardSkeleton />}
      >
        {children}
      </CompanySectionCard>
    </div>
  );
}

type CompanyMobileBodyProps = {
  readonly company: CompanyDTO;
};

export default function CompanyMobileBody({ company }: CompanyMobileBodyProps) {
  const { t, currentLanguage } = useTranslation();
  const arePeopleVisible = useArePeopleVisible();
  const { latestData } = useCompanyEnrichment(company.id);
  const displayedCompany = latestData ?? company;
  const web = useCompanyPageWebProfile(displayedCompany);
  const webKeyData = settledWebKeyData(web.state);

  const [isSearchSheetOpen, setIsSearchSheetOpen] = useState(false);

  const phone = displayedCompany.contact?.phones?.[0]?.phone;
  const email = displayedCompany.contact?.emails?.[0]?.email;
  const hasContactCtas = !!phone || !!email;

  const mapsQuery = getGoogleMapsEmbedQuery(displayedCompany);

  const headerContactCtas = hasContactCtas ? (
    <div className="grid grid-cols-2 gap-2">
      {phone && (
        <Button asChild className="h-11 w-full active:brightness-90">
          <a href={`tel:${phone}`}>
            <PhoneIcon className="w-4 h-4 shrink-0" />
            <span className="truncate">{phone}</span>
          </a>
        </Button>
      )}
      {email && (
        <Button asChild className="h-11 w-full active:brightness-90">
          <a href={`mailto:${email}`}>
            <MailIcon className="w-4 h-4 shrink-0" />
            <span className="truncate">{email}</span>
          </a>
        </Button>
      )}
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <CompanySidePanelBody
        company={displayedCompany}
        headerContactCtas={headerContactCtas}
        hidePhoneAndEmailRows
        hideLegalInfoSection
        hideTechnologiesSection
        headerClassName="bg-gradient-to-br from-primary/5 to-transparent px-4 pt-8 pb-6 border-b border-border"
        bodyClassName="px-4 pt-4 space-y-4"
        topExecutiveSlot={
          <CardCompanyEmployeesSuggested
            company={displayedCompany}
            onSeeMoreClick={() => setIsSearchSheetOpen(true)}
            variant="section"
          />
        }
        // The web profile as the desktop page shows it: its synthesis under the
        // activity, its figures where the base has none, its takeaways, or
        // why it found none — with a retry when asking again may help.
        headerSummary={<CompanyWebSynthesisBox state={web.state} />}
        webFigures={
          webKeyData
            ? webFigureTiles(webKeyData, web.tag, false)
            : undefined
        }
      >
        <CompanyWebHighlights
          highlights={webHighlightsOf(web.state)}
          sources={webSourcesOf(web.state)}
          isWriting={webSynthesisOf(web.state).isWriting}
        />
        <CompanyWebProfileNotice state={web.state} onRetry={web.retry} />
      </CompanySidePanelBody>

      <CompanyMobileAIInsights company={displayedCompany} />

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.events}
        hasData={hasCompanyEventsData(displayedCompany, arePeopleVisible)}
      >
        <CardCompanyEvents company={displayedCompany} />
      </MobileSectionCard>

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.headcountHistory}
        hasData={hasHeadcountHistoryData(displayedCompany)}
      >
        <CardCompanyHeadcountHistory company={displayedCompany} />
      </MobileSectionCard>

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.financialRatios}
        hasData={hasFinancialRatiosData(displayedCompany)}
      >
        <FinancialMetricGrid>
          <CardFinancialRatios company={displayedCompany} />
        </FinancialMetricGrid>
      </MobileSectionCard>

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.googleReviews}
        hasData={hasGoogleReviewsData(displayedCompany)}
      >
        <CardGoogleReviews company={displayedCompany} />
      </MobileSectionCard>

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.employeeReviews}
        hasData={hasEmployeeReviewsData(displayedCompany)}
      >
        <CardEmployeeReviews company={displayedCompany} />
      </MobileSectionCard>

      <CompanySectionCard
        sections={COMPANY_CARD_SECTIONS.location}
        hasData={mapsQuery !== null}
        skeleton={
          <div className="px-4">
            <div className="w-full h-[240px] rounded-xl bg-muted/30" />
          </div>
        }
      >
        <div className="px-4">
          <LazyMount
            className="block w-full h-[240px] rounded-xl overflow-hidden border border-border"
            placeholder={<div className="w-full h-full bg-muted/30" />}
          >
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=${
                import.meta.env.VITE_GOOGLE_MAPS_API_KEY
              }&q=${mapsQuery}`}
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={t("company.insights.location")}
            />
          </LazyMount>
        </div>
      </CompanySectionCard>

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.legalIdentity}
        hasData={
          pickLegalInfoRows(displayedCompany, t, currentLanguage).length > 0
        }
      >
        <CompanyLegalInfoSection company={displayedCompany} />
      </MobileSectionCard>

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.technologies}
        hasData={hasTechnologiesData(displayedCompany)}
      >
        <CompanyTechnologiesSection company={displayedCompany} />
      </MobileSectionCard>

      <MobileSectionCard
        sections={COMPANY_CARD_SECTIONS.market}
        hasData={hasMarketData(displayedCompany)}
      >
        <CompanyMarketSection company={displayedCompany} />
      </MobileSectionCard>

      <CompanyMobileEmployeeSearchSheet
        company={displayedCompany}
        open={isSearchSheetOpen}
        onOpenChange={setIsSearchSheetOpen}
      />
    </div>
  );
}
